/**
 * Zustand store — JWT + profile state.
 *
 * Persisted to localStorage so the web-login flow (email+password, no
 * LINE) can survive page reloads. The LIFF flow doesn't need this —
 * /auth/me always re-issues a JWT from the LIFF idToken on entry —
 * but persisting it just makes that re-issue a no-op cache hit.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthProfile } from '@payroll/shared';

interface AuthState {
  jwt: string | null;
  profile: AuthProfile | null;
  setAuth: (jwt: string, profile: AuthProfile) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      jwt: null,
      profile: null,
      setAuth: (jwt, profile) => set({ jwt, profile }),
      clear: () => set({ jwt: null, profile: null }),
    }),
    { name: 'paypilot-auth' },
  ),
);

/** Synchronous JWT check used at boot (before React mounts) to decide
 *  whether to skip LIFF init and fall back to the web-login flow. */
export function hasPersistedJwt(): boolean {
  try {
    const raw = localStorage.getItem('paypilot-auth');
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { state?: { jwt?: string | null } };
    return !!parsed?.state?.jwt;
  } catch {
    return false;
  }
}
